import React from "react";
import { Image as ImageIcon, Loader2 } from "lucide-react";

/**
 * Reusable logo upload field component
 * 
 * @param {Object} props Component properties
 * @param {string} props.title Title of the logo upload field
 * @param {string} props.description Description text
 * @param {string} props.preview URL or data URL for preview
 * @param {number} props.progress Upload progress percentage
 * @param {Function} props.onChange Change handler for file input
 * @param {React.ReactNode} props.icon Icon component to display
 * @param {string} props.type Type identifier for the logo (e.g., 'company', 'convention')
 * @param {boolean} props.isUploading Whether an upload is in progress
 */
const LogoUploadField = ({
  title,
  description,
  preview,
  progress,
  onChange,
  icon,
  type,
  isUploading,
}) => {
  const handleImageError = (e) => {
    console.warn(`Error loading ${type} image. URL might be expired.`);
    e.target.src = "/placeholder-logo.png"; // Fallback to a default image
  };

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        {icon}
        {title}
      </h3>
      <p className="text-sm opacity-70 mb-2">{description}</p>

      <div className="flex flex-col items-center gap-4 sm:flex-row">
        {/* Preview with error handling */}
        <div
          className="relative w-40 h-40 bg-base-200 rounded-lg flex items-center justify-center overflow-hidden border border-base-300"
          aria-label={`${title} preview`}
        >
          {preview ? (
            <img
              src={preview}
              alt={`${title} Preview`}
              className="max-w-full max-h-full object-contain"
              onError={(e) => handleImageError(e)}
            />
          ) : (
            <ImageIcon className="w-12 h-12 opacity-30" aria-hidden="true" />
          )}

          {/* Upload Progress Overlay */}
          {progress > 0 && (
            <div
              className="absolute inset-0 bg-base-100/70 backdrop-blur-sm flex flex-col items-center justify-center"
              aria-live="polite"
              aria-atomic="true"
            >
              <Loader2
                className="w-8 h-8 animate-spin mb-2 text-primary"
                aria-hidden="true"
              />
              <span className="text-sm font-medium">{progress}% uploaded</span>
            </div>
          )}
        </div>

        {/* Upload Button */}
        <div className="flex flex-col gap-2 flex-1">
          <label className="btn btn-outline">
            {preview ? "Change Image" : "Upload Image"}
            <input
              type="file"
              className="hidden"
              onChange={onChange}
              accept="image/*"
              disabled={isUploading}
              aria-label={`Upload ${title}`}
            />
          </label>
          <span className="text-xs opacity-70">
            Recommended size: 200×100px. Max 5MB.
          </span>

          {preview && (
            <div className="text-xs text-info mt-1">
              <span>Image selected. Click "Save Settings" to upload.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogoUploadField; 